import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getOperator } from "@/data/operators";

const EASE = [0.16, 1, 0.3, 1];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function OperatorDetail() {
  const { slug } = useParams();
  const op = getOperator(slug);
  const navigate = useNavigate();

  const goPortfolio = () => {
    navigate("/", { state: { scrollTo: "portfolio" } });
  };

  useEffect(() => {
    if (!op) return;
    const prev = document.title;
    document.title = `${op.name} — Govil Ventures`;
    return () => {
      document.title = prev;
    };
  }, [op]);

  if (!op || !op.detail) return <Navigate to="/operators" replace />;

  return (
    <div data-testid="operator-detail-page" className="min-h-screen bg-noir text-cream">
      <section className="px-6 pt-32 pb-20 md:px-12 md:pt-40 md:pb-28">
        <div className="mx-auto max-w-7xl">
          {/* back */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <Link
              data-testid="operator-back-link"
              to="/operators"
              className="group inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-mute transition-colors duration-300 hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
              All Operators
            </Link>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
            {/* portrait */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
                className="relative mx-auto w-full max-w-[440px] overflow-hidden rounded-[6px] lg:mx-0"
                style={{ aspectRatio: "4 / 5" }}
              >
                {op.image ? (
                  <img
                    data-testid="operator-portrait"
                    src={op.image}
                    alt={`${op.name}, ${op.role} of ${op.company}`}
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  <div
                    data-testid="operator-portrait"
                    className="flex h-full w-full items-center justify-center bg-[linear-gradient(155deg,#171717_0%,#0d0d0d_100%)]"
                  >
                    <span className="font-serif text-[7rem] font-bold text-gold/25">
                      {initials(op.name)}
                    </span>
                  </div>
                )}
                <div className="pointer-events-none absolute inset-0 [background:linear-gradient(160deg,rgba(201,162,39,0.10)_0%,transparent_45%,rgba(10,10,10,0.28)_100%)]" />
                <motion.span
                  aria-hidden
                  className="absolute bottom-0 left-0 h-px w-full origin-left bg-gold"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
                />
              </motion.div>
            </div>

            {/* content */}
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                className="mb-5 text-[0.72rem] font-semibold uppercase tracking-[0.28em] text-gold"
              >
                {op.role} &middot; {op.company}
              </motion.p>
              <motion.h1
                data-testid="operator-name"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
                className="font-serif font-bold leading-[0.98] tracking-tight text-cream"
                style={{ fontSize: "clamp(2.6rem, 5.2vw, 4.4rem)" }}
              >
                {op.name}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
                className="mt-6 max-w-xl text-[1.15rem] leading-relaxed text-cream/85"
              >
                {op.short}
              </motion.p>

              {/* bio */}
              <div className="mt-10 max-w-[62ch] space-y-6 text-[1.05rem] leading-[1.75] text-cream/75">
                {op.bio.map((para, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.7, ease: EASE }}
                  >
                    {para}
                  </motion.p>
                ))}
              </div>

              {/* signature quote */}
              {op.quote && (
                <motion.blockquote
                  data-testid="operator-quote"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.8, ease: EASE }}
                  className="mt-12 border-l-2 border-gold pl-6 md:pl-8"
                >
                  <p className="font-quote text-[1.7rem] leading-[1.3] text-cream md:text-[2.15rem]">
                    &ldquo;{op.quote}&rdquo;
                  </p>
                  <footer className="mt-4 text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-gold">
                    {op.name} &middot; {op.company}
                  </footer>
                </motion.blockquote>
              )}

              {/* focus areas */}
              <div className="mt-12 border-t border-white/10 pt-8">
                <p className="mb-4 text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-mute">
                  What they bring
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {op.focus.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-white/15 px-4 py-1.5 text-[0.72rem] font-medium tracking-wide text-cream/80"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* company wordmark — portfolio linkage */}
              <div
                data-testid="operator-company-linkage"
                className="mt-12 overflow-hidden rounded-[6px] border border-white/10 bg-[#111111]"
              >
                <div className="flex flex-col gap-5 p-7 sm:flex-row sm:items-center sm:justify-between md:p-8">
                  <div>
                    <p className="mb-2 text-[0.62rem] font-semibold uppercase tracking-[0.24em] text-mute">
                      Building
                    </p>
                    <span
                      data-testid="operator-company-wordmark"
                      className="font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl"
                    >
                      {op.company}
                    </span>
                    <p className="mt-2 text-[0.7rem] font-medium uppercase tracking-[0.18em] text-gold">
                      {op.companyTag}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="operator-portfolio-link"
                    onClick={goPortfolio}
                    className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-white/25 px-7 py-2.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    View in portfolio
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
